// hooks/useStudentGroups.js
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { studentService } from '../Services/studentService';
import { setSuccessSnack } from '../Reducers/snackSuccess';
import { handleApiError } from '../Utils/ApiErrorHandler';
import { setSnackError } from '../Reducers/snackErrors';
import { DateTime } from "luxon";

export const useGetStudents = () => {
  const dispatch = useDispatch();

  const [students, setStudents] = useState([]);
  const [studentInfo, setStudentInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pollingKey, setPollingKey] = useState(null);


  const pollStudentFileDownload = useCallback(async(inputKey, onSuccess) => {
    const POLL_INTERVAL = 6000;
    const MAX_POLL_TIME = 80000; 
    const MAX_ATTEMPTS = Math.floor(MAX_POLL_TIME / POLL_INTERVAL);

    let attempts = 0;
    let intervalId;
  
    const poll = async () => {
      attempts++;

      try {
        const data = await studentService.getStudentFileUrl(inputKey);
        const { status, url } = data;  
        if (status === 'DONE') {
          clearInterval(intervalId);
          setDownloading(false);
          setPollingKey(null);
          if (onSuccess) onSuccess(url);
          return;
        }
  
        if (status === 'ERROR') {
          clearInterval(intervalId);
          setDownloading(false);
          setPollingKey(null);
          dispatch(setSnackError({
            title: 'Error',
            body: 'Backend error, this is an unusual issue.'
          }));
          return;
        }

        if (attempts >= MAX_ATTEMPTS) {
          clearInterval(intervalId);
          setDownloading(false);
          setPollingKey(null);
          dispatch(setSnackError({
            title: 'Error',
            body: 'Servers are running slow. Try again later.'
          }));
          return;
        }
  
        if (status === 'RETRY' || status === 'PENDING') {
          return;
        }
  
      } catch (error) {
        clearInterval(intervalId);
        setDownloading(false);
        setPollingKey(null);
        handleApiError(error, dispatch, 'download student file');
      }
    };
  
    // Start polling
    setDownloading(true);
    setPollingKey(inputKey);
    intervalId = setInterval(poll, POLL_INTERVAL);
    
    // Initial immediate call
    poll();
  
    // Cleanup function
    return () => {
      clearInterval(intervalId);
      setPollingKey(null);
    }
  }, []);

  const requestStudentFile = async (values, sortKey) => {
    try {
      setLoading(true);
      const formattedValues = { ...values };
      if (values.date) { formattedValues.date = DateTime.fromJSDate(new Date(values.date)).toSQLDate(); }
      if (values.date_end) { formattedValues.date_end = DateTime.fromJSDate(new Date(values.date_end)).toSQLDate(); }
      
      // Request file with sort key
      const inputKey = await studentService.getStudentFile({
        ...formattedValues,
        sort_key: sortKey
      });
      
      if (inputKey) {
        return inputKey;
      }
      return null;
    } catch (error) {
      handleApiError(error, dispatch, 'request student file');
      return null;
    } finally {
      setLoading(false);
    }
  }

  const searchForStudents = async (payload) => {
    setLoading(true);
    try {
      const data = await studentService.searchForStudents(payload);
      setStudents(data);
      return data;
    } catch (error) {
      handleApiError(error, dispatch, "searching for students")
      return null;
    } finally {
      setLoading(false);
    }
  }

  const getStudentInfo = async (student_id) => {
    setLoading(true);
    try {
      const data = await studentService.getStudentInfo(student_id);
      setStudentInfo(data);
      return data;
    } catch (error) {
      handleApiError(error, dispatch, "searching for students info")
      return null;
    } finally {
      setLoading(false);
    }
  }

  const fetchStudents = async (locationId) => {
    if (!locationId) return null;
    setLoading(true);
    try {
      const data = await studentService.getStudents(locationId);
      setStudents(data);
      return data;
    } catch (error) {
      handleApiError(error, dispatch, "fetching for students")
      return null;
    } finally {
      setLoading(false);
    }
  };


  const deleteStudent = async (id, location_id) => {
    if (!id) { return ;}
    setDeleting(true);
    try {
      const data = await studentService.deleteStudent(id, location_id);
      if (data.status === "Removed") {
        dispatch(setSuccessSnack({title: 'Success', body:'Removed student'}));
        return true
      }
      return false;
    } catch (error) {
      handleApiError(error, dispatch, "deleting students")
      return false;
    } finally {
      setDeleting(false);
    }
  };
  

  return { 
    studentInfo,
    downloading,
    students, 
    loading,
    deleting,
    fetchStudents,
    deleteStudent,
    searchForStudents,
    getStudentInfo,
    pollStudentFileDownload,
    requestStudentFile
  };
};